import React, { useContext } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import AccountIcon from '@components/dashboard/components/AccountIcon';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import { NavContext } from '@components/navigation/Context';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import { ABOUT_US_URL, HEADER_CLOSE_SVG, HELP_URL, VITALS_URL, BREAKPOINT } from '@constants';
import { PageOptions, NavBarCategory, NavStatus } from '@contracts';
import IconAccount from '@images/account.svg';
import IconCart from '@images/cart.svg';
import IconSearch from '@images/search.svg';
import { pageIsAccountPage, pageIsFocusPage } from '@utils/helpers';
import { getParams } from '@utils/history';
import { viewCartTrack } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';
import { useRedirectUrl } from '@utils/useRedirectUrl';
import NavBarLink, { NavBarLinksWrap } from './NavBarLink';

interface Props {}

const NavBarRight: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { state } = useContext(Context);
  const { navState, navDispatch } = useContext(NavContext);
  const { cartItems, loggedIn } = state;
  const { status, basketCount, navbarItemColor, navbarSelectedCategories } = navState;
  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const desktop = useDesktop(BREAKPOINT.laptop);
  const location = useLocation();
  const isCheckoutPage = location.pathname.includes('/checkout/');
  const isAccountPage = pageIsAccountPage(location.pathname);
  const isFocusPage = pageIsFocusPage(location.pathname);
  const isPPCPage = location.pathname.includes('/ppc-questionnaire');
  const isSearch = navbarSelectedCategories?.length ? navbarSelectedCategories.includes(NavBarCategory.Search) : false;
  const params = getParams();
  const consultationReferrer = !!params.consultation;
  const { redirectUrl } = useRedirectUrl();

  const handleNavBarBasketClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Basket });
    viewCartTrack(cartItems);
  };

  const handleNavBarSearchClick = () => {
    if (isSearch) {
      navDispatch({ type: 'setStatus', data: NavStatus.Default });
      navDispatch({ type: 'removeNavbarSelectedCategories' });
    } else {
      navDispatch({ type: 'removeNavbarSelectedCategories', data: NavBarCategory.Search });
      navDispatch({ type: 'setStatus', data: NavStatus.Menu });
    }
  };

  const handleGoToAccount = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    let url;

    if (loggedIn) {
      if (!desktop) {
        url = '/dashboard/?nav=true';
      } else {
        url = '/dashboard/';
      }
    } else {
      url = '/dashboard/login/';
    }

    navigateWrapper(url);
  };

  const handleGoHome = () => {
    navigateWrapper(redirectUrl || '/');
  };

  const focusGoBack = () => {
    if (consultationReferrer) {
      navigateWrapper('/ppc-questionnaire/?questionnaireContinue=true');
    } else {
      const url = loggedIn ? '/dashboard/' : '/';
      navigateWrapper(url);
    }
  };

  const handleAccountClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    const url = `/dashboard/${PageOptions.AccountSettings}/`;
    navigateWrapper(url);
  };

  if (isCheckoutPage) {
    return (
      <FocusCloseButton onClick={handleGoHome}>
        <img src={HEADER_CLOSE_SVG} alt="flow-close" />
      </FocusCloseButton>
    );
  }

  if (isPPCPage) {
    return null;
  }

  if (isFocusPage) {
    return (
      <FocusCloseButton onClick={focusGoBack}>
        <img src={HEADER_CLOSE_SVG} alt="flow-close" />
      </FocusCloseButton>
    );
  }

  if (isAccountPage) {
    return (
      <AccountPageWrap>
        <DashboardCartIconWrap>
          <IconCart onClick={() => handleNavBarBasketClick()} />
          {basketCount && basketCount > 0 ? <BasketCount>{basketCount}</BasketCount> : null}
        </DashboardCartIconWrap>
        <AccountIconWrap onClick={handleAccountClick}>
          <AccountIcon icon={userInfo.profileIcon} />
        </AccountIconWrap>
      </AccountPageWrap>
    );
  }

  return (
    <>
      <NavBarLinksWrap>
        <NavBarLink text={t('components.about-us')} category={NavBarCategory.AboutUs} navbarLink={ABOUT_US_URL} />
        <NavBarLink text={t('components.vitals-blog')} navbarLink={VITALS_URL} />
        <NavBarLink text={t('components.help-center-link')} navbarLink={HELP_URL} />
      </NavBarLinksWrap>
      <SearchIconWrap onClick={() => handleNavBarSearchClick()}>
        <Search $color={navbarItemColor} $active={isSearch} />
      </SearchIconWrap>
      <CartIconWrap onClick={() => handleNavBarBasketClick()}>
        <Cart $color={navbarItemColor} $active={status === NavStatus.Basket} />
        {basketCount && basketCount > 0 ? <BasketCount>{basketCount}</BasketCount> : null}
      </CartIconWrap>
      <AccountStyled $color={navbarItemColor} onClick={handleGoToAccount} $active={status === NavStatus.Basket} />
    </>
  );
};

export default NavBarRight;

const CartIconWrap = styled.div<{ $color?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;

  span {
    position: absolute;
    z-index: 9;
    left: 16px;
    font-size: 12px;
    color: #fff;
    background: ${(props) => props.$color};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    text-align: center;
    line-height: 20px;
    font-family: 'BrownStd';
    font-weight: bold;
  }
`;

const DashboardCartIconWrap = styled(CartIconWrap)`
  cursor: pointer;

  svg > g > g {
    fill: ${Colors.navyBlue};
  }
`;

const Cart = styled(IconCart)`
  cursor: pointer;
  transition: 0.2s all ease;

  path {
    fill: ${(props) => props.$color};
  }

  &:hover {
    opacity: 0.6;
  }

  ${(props) => {
    if (props.$active) {
      return `
        g > path {
          fill:  ${Colors.forestGreen};
        }
      `;
    }

    return '';
  }};

  @media (max-width: 768px) {
    width: 21px;
    height: 26px;
  }
`;

const SearchIconWrap = styled.div<{ $color?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;

  span {
    position: absolute;
    z-index: 9;
    left: 16px;
    font-size: 12px;
    color: #fff;
    background: ${(props) => props.$color};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    text-align: center;
    line-height: 20px;
    font-family: 'BrownStd';
    font-weight: bold;
  }
`;

const Search = styled(IconSearch)`
  cursor: pointer;
  transition: 0.2s all ease;

  path {
    fill: ${(props) => props.$color};
  }

  &:hover {
    opacity: 0.6;
  }

  ${(props) => {
    if (props.$active) {
      return `
        g > path {
          fill:  ${Colors.forestGreen};
        }
      `;
    }

    return '';
  }};

  @media (max-width: 768px) {
    width: 21px;
    height: 26px;
  }
`;

const AccountStyled = styled(IconAccount)`
  cursor: pointer;
  transition: 0.2s all ease;

  path {
    fill: ${(props) => props.$color};
  }

  &:hover {
    opacity: 0.6;
  }

  ${(props) => {
    if (props.$active) {
      return `
        g > path {
          fill:  ${Colors.forestGreen};
        }
      `;
    }

    return '';
  }};

  @media (max-width: 768px) {
    width: 20px;
    height: 26px;
  }
`;

const FocusCloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 5px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }

  & > img {
    width: 20px;
    height: 20px;
  }
`;

const BasketCount = styled.span`
  background: ${Colors.gold};
  color: ${Colors.cream};
  user-select: none;
`;

const AccountPageWrap = styled.div`
  display: flex;
  gap: 20px;
`;

const AccountIconWrap = styled.div`
  display: flex;
  cursor: pointer;
`;
